import { useEffect } from "react";

import { useMyProfile } from "@/hooks/use-my-profile";
import {
  LOCALE_COOKIE,
  localisePath,
  normaliseLocale,
  persistLocaleCookie,
  useLocale,
} from "@/i18n";

function hasLocaleCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${LOCALE_COOKIE}=`));
}

/**
 * Applies a signed-in member's saved language once per session: only when they
 * have never made an explicit choice on this device (no cookie yet) and the
 * current URL carries no locale prefix. A manual selection is therefore never
 * overridden.
 */
export function useLocalePreferenceSync() {
  const { data: profile } = useMyProfile();
  const locale = useLocale();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const saved = normaliseLocale(profile?.preferred_locale);
    if (!saved) return;
    if (hasLocaleCookie()) return;
    persistLocaleCookie(saved);
    if (saved === locale) return;
    // Safari blocks cookies in embedded/third-party contexts. If the choice
    // didn't stick, a reload would re-detect the same locale and loop forever.
    if (!hasLocaleCookie()) return;
    window.location.replace(localisePath(window.location.pathname, saved) + window.location.search);
  }, [profile?.preferred_locale, locale]);
}
